import { ArrowLeft, ExternalLink, FileText, UploadCloud, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { PageHeader } from '../../components/common/PageHeader';
import { StatusBadge } from '../../components/common/StatusBadge';
import { getEmployee, updateEmployee } from '../../firebase/firestore';
import { storage } from '../../firebase/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useToast } from '../../contexts/ToastContext';

const EMPTY_FORM = {
  fullName: '', jobTitle: '', email: '', phone: '', status: 'active', employmentType: 'full_time', startDate: '',
  nationalId: '', passportNumber: '', identityDocumentUrl: '', identityDocumentName: '', salary: '', salaryFrequency: 'monthly',
  paymentMethod: 'bank_transfer', bankName: '', bankAccountName: '', bankAccountNumber: '', address: '',
  emergencyContactName: '', emergencyContactPhone: '', notes: '',
};

function getEmployeeFormValues(employee) {
  return Object.keys(EMPTY_FORM).reduce((values, key) => ({
    ...values,
    [key]: employee?.[key] ?? EMPTY_FORM[key],
  }), {});
}

export function EmployeeEditPage() {
  const { employeeId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { activeTenantId, role } = useTenant();
  const { notify } = useToast();
  const currentUser = employeeId === 'current-user' ? {
    id: 'current-user',
    fullName: user?.displayName || user?.email || 'Current user',
    jobTitle: role?.name || 'Account owner',
    email: user?.email || '',
    phone: '',
    status: 'active',
    isCurrentUser: true,
  } : null;
  const [employee, setEmployee] = useState(location.state?.employee || currentUser);
  const [loading, setLoading] = useState(!currentUser);
  const [formValues, setFormValues] = useState(() => getEmployeeFormValues(location.state?.employee || currentUser));
  const [documentFile, setDocumentFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (employee) setFormValues(getEmployeeFormValues(employee));
  }, [employee]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getEmployee(activeTenantId, employeeId)
      .then((nextEmployee) => {
        if (mounted && nextEmployee) setEmployee(nextEmployee);
      })
      .catch((error) => console.error('Unable to load employee', error))
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [activeTenantId, employeeId]);

  function handleFieldChange(event) {
    const { name, value } = event.target;
    setFormValues((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!activeTenantId || !formValues.fullName.trim() || saving) return;

    if (documentFile && documentFile.size > 5 * 1024 * 1024) {
      notify('The identity document must be 5 MB or smaller.', 'error');
      return;
    }

    setSaving(true);
    let nextValues = { ...formValues };
    try {
      if (documentFile) {
        setUploading(true);
        const safeName = documentFile.name.replace(/[^a-zA-Z0-9._-]/g, '-');
        const documentRef = ref(storage, `tenants/${activeTenantId}/employees/${employeeId}/${Date.now()}-${safeName}`);
        const snapshot = await uploadBytes(documentRef, documentFile, { contentType: documentFile.type });
        nextValues = {
          ...nextValues,
          identityDocumentUrl: await getDownloadURL(snapshot.ref),
          identityDocumentName: documentFile.name,
        };
        setUploading(false);
      }

      await updateEmployee(activeTenantId, employeeId, user?.uid, nextValues);
      const updatedEmployee = { ...employee, ...nextValues };
      setEmployee(updatedEmployee);
      setFormValues(getEmployeeFormValues(updatedEmployee));
      setDocumentFile(null);
      notify('Employee details updated successfully.');
    } catch (error) {
      console.error('Unable to update employee', error);
      notify('Unable to update employee details. Try again.', 'error');
    } finally {
      setUploading(false);
      setSaving(false);
    }
  }

  if (loading) return <LoadingState label="Loading employee..." />;

  if (!employee) {
    return (
      <div className="employees-page">
        <Link className="employees-back-link" to={`/dashboard/hr/employees/${employeeId}`}><ArrowLeft size={16} /> Back to Employee</Link>
        <EmptyState title="Employee not found." message="This employee record may have been removed or the link is invalid." icon={UserRound} />
      </div>
    );
  }

  return (
    <div className="employees-page employee-detail-page">
      <Link className="employees-back-link" to={`/dashboard/hr/employees/${employeeId}`}><ArrowLeft size={16} /> Back to Employee</Link>
      <PageHeader title={employee.fullName || 'Employee details'} description="Update employee information, documents, compensation and payment details." action={<Button type="submit" form="employee-details-form" loading={saving}>{uploading ? 'Uploading...' : 'Save changes'}</Button>} />
      <form id="employee-details-form" className="employee-detail-card employee-detail-form" onSubmit={handleSubmit}>
        <div className="employee-details-avatar">{employee.fullName?.trim()?.charAt(0)?.toUpperCase() || '?'}</div>
        <div className="employee-details-name">
          <h2>{employee.fullName || 'Unnamed employee'}</h2>
          {employee.isCurrentUser ? <span className="employees-current-badge">Logged in</span> : null}
        </div>
        <section className="employee-form-section">
          <div className="employee-form-section-heading"><FileText size={17} /><div><h3>Basic information</h3><p>Keep the employee’s contact and employment details current.</p></div></div>
          <div className="employee-form-grid">
            <FormField label="Full name" id="employee-detail-name"><input id="employee-detail-name" name="fullName" required value={formValues.fullName} onChange={handleFieldChange} /></FormField>
            <FormField label="Job title" id="employee-detail-job"><input id="employee-detail-job" name="jobTitle" value={formValues.jobTitle} onChange={handleFieldChange} placeholder="e.g. Admissions officer" /></FormField>
            <FormField label="Email" id="employee-detail-email"><input id="employee-detail-email" name="email" type="email" value={formValues.email} onChange={handleFieldChange} /></FormField>
            <FormField label="Phone number" id="employee-detail-phone"><input id="employee-detail-phone" name="phone" type="tel" value={formValues.phone} onChange={handleFieldChange} /></FormField>
            <FormField label="Employment type" id="employee-employment-type"><select id="employee-employment-type" name="employmentType" value={formValues.employmentType} onChange={handleFieldChange}><option value="full_time">Full time</option><option value="part_time">Part time</option><option value="contract">Contract</option><option value="casual">Casual</option></select></FormField>
            <FormField label="Status" id="employee-detail-status"><select id="employee-detail-status" name="status" value={formValues.status} onChange={handleFieldChange}><option value="active">Active</option><option value="on_leave">On leave</option><option value="inactive">Inactive</option></select></FormField>
            <FormField label="Start date" id="employee-start-date"><input id="employee-start-date" name="startDate" type="date" value={formValues.startDate} onChange={handleFieldChange} /></FormField>
            <FormField label="Address" id="employee-address"><input id="employee-address" name="address" value={formValues.address} onChange={handleFieldChange} /></FormField>
          </div>
        </section>

        <section className="employee-form-section">
          <div className="employee-form-section-heading"><FileText size={17} /><div><h3>Identity documents</h3><p>Store an ID or passport number and keep a copy with the employee record.</p></div></div>
          <div className="employee-form-grid">
            <FormField label="National ID number" id="employee-national-id"><input id="employee-national-id" name="nationalId" value={formValues.nationalId} onChange={handleFieldChange} /></FormField>
            <FormField label="Passport number" id="employee-passport"><input id="employee-passport" name="passportNumber" value={formValues.passportNumber} onChange={handleFieldChange} /></FormField>
          </div>
          <label className="employee-upload-field" htmlFor="employee-document">
            <span className="employee-upload-icon"><UploadCloud size={18} /></span>
            <span><strong>{documentFile ? documentFile.name : formValues.identityDocumentName ? 'Replace identity document' : 'Upload passport or ID copy'}</strong><small>PDF, JPG or PNG up to 5 MB</small></span>
            <input id="employee-document" type="file" accept="application/pdf,image/jpeg,image/png" onChange={(event) => setDocumentFile(event.target.files?.[0] || null)} />
          </label>
          {formValues.identityDocumentUrl ? <a className="employee-document-link" href={formValues.identityDocumentUrl} target="_blank" rel="noreferrer"><ExternalLink size={14} /> View saved document</a> : null}
        </section>

        <section className="employee-form-section">
          <div className="employee-form-section-heading"><FileText size={17} /><div><h3>Compensation &amp; payment</h3><p>Record salary information and how the employee is paid.</p></div></div>
          <div className="employee-form-grid">
            <FormField label="Salary" id="employee-salary"><input id="employee-salary" name="salary" type="number" min="0" step="0.01" value={formValues.salary} onChange={handleFieldChange} placeholder="0" /></FormField>
            <FormField label="Salary frequency" id="employee-salary-frequency"><select id="employee-salary-frequency" name="salaryFrequency" value={formValues.salaryFrequency} onChange={handleFieldChange}><option value="monthly">Monthly</option><option value="weekly">Weekly</option><option value="daily">Daily</option><option value="hourly">Hourly</option></select></FormField>
            <FormField label="Payment method" id="employee-payment-method"><select id="employee-payment-method" name="paymentMethod" value={formValues.paymentMethod} onChange={handleFieldChange}><option value="bank_transfer">Bank transfer</option><option value="mpesa">M-Pesa</option><option value="cash">Cash</option><option value="cheque">Cheque</option></select></FormField>
            <FormField label="Bank name" id="employee-bank-name"><input id="employee-bank-name" name="bankName" value={formValues.bankName} onChange={handleFieldChange} /></FormField>
            <FormField label="Account name" id="employee-account-name"><input id="employee-account-name" name="bankAccountName" value={formValues.bankAccountName} onChange={handleFieldChange} /></FormField>
            <FormField label="Account number" id="employee-account-number"><input id="employee-account-number" name="bankAccountNumber" value={formValues.bankAccountNumber} onChange={handleFieldChange} /></FormField>
          </div>
        </section>

        <section className="employee-form-section">
          <div className="employee-form-section-heading"><UserRound size={17} /><div><h3>Emergency contact &amp; notes</h3><p>Keep useful context available to the people responsible for HR.</p></div></div>
          <div className="employee-form-grid">
            <FormField label="Emergency contact name" id="employee-emergency-name"><input id="employee-emergency-name" name="emergencyContactName" value={formValues.emergencyContactName} onChange={handleFieldChange} /></FormField>
            <FormField label="Emergency contact phone" id="employee-emergency-phone"><input id="employee-emergency-phone" name="emergencyContactPhone" type="tel" value={formValues.emergencyContactPhone} onChange={handleFieldChange} /></FormField>
          </div>
          <FormField label="Notes" id="employee-notes"><textarea id="employee-notes" name="notes" rows="4" value={formValues.notes} onChange={handleFieldChange} placeholder="Additional HR notes" /></FormField>
        </section>
        <div className="employee-detail-form-actions"><Button type="submit" loading={saving}>{uploading ? 'Uploading...' : 'Save changes'}</Button></div>
      </form>
    </div>
  );
}

export function EmployeeDetailPage() {
  const { employeeId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { activeTenantId, role } = useTenant();
  const currentUser = employeeId === 'current-user' ? {
    id: 'current-user',
    fullName: user?.displayName || user?.email || 'Current user',
    jobTitle: role?.name || 'Account owner',
    email: user?.email || '',
    phone: '',
    status: 'active',
    isCurrentUser: true,
  } : null;
  const [employee, setEmployee] = useState(location.state?.employee || currentUser);
  const [loading, setLoading] = useState(!currentUser);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getEmployee(activeTenantId, employeeId)
      .then((nextEmployee) => {
        if (mounted && nextEmployee) setEmployee(nextEmployee);
      })
      .catch((error) => console.error('Unable to load employee', error))
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [activeTenantId, employeeId]);

  if (loading) return <LoadingState label="Loading employee..." />;

  if (!employee) {
    return (
      <div className="employees-page">
        <Link className="employees-back-link" to="/dashboard/hr/employees"><ArrowLeft size={16} /> Back to Employees</Link>
        <EmptyState title="Employee not found." message="This employee record may have been removed or the link is invalid." icon={UserRound} />
      </div>
    );
  }

  const salary = Number(employee.salary || 0);
  const salaryLabel = salary ? `${salary.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${employee.salaryFrequency || 'monthly'}` : 'Not specified';
  const details = [
    ['Job title', employee.jobTitle || 'Not specified'],
    ['Employment type', String(employee.employmentType || 'Not specified').replace(/_/g, ' ')],
    ['Email', employee.email || 'Not provided'],
    ['Phone', employee.phone || 'Not provided'],
    ['Start date', employee.startDate || 'Not specified'],
    ['Address', employee.address || 'Not provided'],
  ];
  const identity = [
    ['National ID number', employee.nationalId || 'Not provided'],
    ['Passport number', employee.passportNumber || 'Not provided'],
    ['Document', employee.identityDocumentName || 'No document uploaded'],
  ];
  const compensation = [
    ['Salary', salaryLabel],
    ['Payment method', String(employee.paymentMethod || 'Not specified').replace(/_/g, ' ')],
    ['Bank name', employee.bankName || 'Not provided'],
    ['Account name', employee.bankAccountName || 'Not provided'],
    ['Account number', employee.bankAccountNumber || 'Not provided'],
  ];

  return (
    <div className="employees-page employee-profile-page">
      <Link className="employees-back-link" to="/dashboard/hr/employees"><ArrowLeft size={16} /> Back to Employees</Link>
      <section className="employee-profile-hero">
        <div className="employee-details-avatar">{employee.fullName?.trim()?.charAt(0)?.toUpperCase() || '?'}</div>
        <div className="employee-profile-hero-copy">
          <p className="employees-kicker">HR &amp; people operations</p>
          <div className="employee-details-name"><h1>{employee.fullName || 'Unnamed employee'}</h1>{employee.isCurrentUser ? <span className="employees-current-badge">Logged in</span> : null}</div>
          <p>{employee.jobTitle || 'Employee profile'} · {activeTenantId ? 'Workspace record' : 'Employee record'}</p>
        </div>
        <Link className="btn btn-primary employee-profile-edit-btn" to={`/dashboard/hr/employees/${employeeId}/edit`} state={{ employee }}>Update this information</Link>
      </section>

      <div className="employee-profile-status-row">
        <div><span>Employee status</span><strong><StatusBadge status={employee.status || 'active'} /></strong></div>
        <div><span>Salary</span><strong>{salaryLabel}</strong></div>
        <div><span>Payment method</span><strong>{String(employee.paymentMethod || 'Not specified').replace(/_/g, ' ')}</strong></div>
      </div>

      <div className="employee-profile-grid">
        <section className="employee-profile-card">
          <div className="employee-profile-card-heading"><UserRound size={17} /><div><p>Profile</p><h2>Basic information</h2></div></div>
          <div className="employee-profile-fields">{details.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
        </section>
        <section className="employee-profile-card">
          <div className="employee-profile-card-heading"><FileText size={17} /><div><p>Verification</p><h2>Identity documents</h2></div></div>
          <div className="employee-profile-fields">{identity.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
          {employee.identityDocumentUrl ? <a className="employee-document-link" href={employee.identityDocumentUrl} target="_blank" rel="noreferrer"><ExternalLink size={14} /> View saved document</a> : null}
        </section>
      </div>

      <section className="employee-profile-card">
        <div className="employee-profile-card-heading"><FileText size={17} /><div><p>Compensation</p><h2>Payment details</h2></div></div>
        <div className="employee-profile-fields employee-profile-fields--wide">{compensation.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
      </section>

      <section className="employee-profile-card employee-profile-card--small">
        <div className="employee-profile-card-heading"><UserRound size={17} /><div><p>Support</p><h2>Emergency contact &amp; notes</h2></div></div>
        <div className="employee-profile-fields employee-profile-fields--wide">
          <div><span>Emergency contact</span><strong>{employee.emergencyContactName || 'Not provided'}</strong></div>
          <div><span>Emergency phone</span><strong>{employee.emergencyContactPhone || 'Not provided'}</strong></div>
          <div className="employee-profile-note"><span>Notes</span><strong>{employee.notes || 'No notes added.'}</strong></div>
        </div>
      </section>
    </div>
  );
}
